// EditCasePage.jsx
import React, { useState, useEffect } from "react";
import axios from "axios";
import { useParams, useNavigate } from "react-router-dom";
import {
  Container,
  Row,
  Col,
  Card,
  Form,
  Button,
  Alert,
  ProgressBar,
  Tab,
  Tabs,
  Modal,
  Badge,
  Stack,
  Accordion,
  Tooltip,
  OverlayTrigger,
  Spinner,
  Dropdown,
  InputGroup,
} from "react-bootstrap";
import {
  FaEdit,
  FaSave,
  FaArrowLeft,
  FaCamera,
  FaUpload,
  FaTrash,
  FaPlus,
  FaEye,
  FaCheck,
  FaTimes,
  FaChevronLeft,
  FaChevronRight,
  FaImage,
  FaFileSignature,
  FaVideo,
} from "react-icons/fa";
import { useDropzone } from "react-dropzone";
import { Baseurl, Imageurl } from "../../baseurl";

const EditCase = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({});
  const [caseData, setCaseData] = useState(null);
  const [agents, setAgents] = useState([]);
  const [inspectors, setInspectors] = useState([]);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [activeStep, setActiveStep] = useState(0);
  const [tabValue, setTabValue] = useState(0);
  const [showImageModal, setShowImageModal] = useState(false);
  const [currentImageType, setCurrentImageType] = useState("");
  const [newImage, setNewImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);

  // Vehicle type configuration
  const vehicleTypes = [
    "evTwoWheeler",
    "evThreeWheeler",
    "evFourWheeler",
    "evCommercial",
    "evBus",
  ];

  const caseTypes = ["Inspection", "Valuation", "Repair", "Maintenance"];
  const vehicleStatuses = [
    "Parked",
    "Idle",
    "Maintenance",
    "Service Requested",
    "Place to Auction",
    "Need Refurbishment",
  ];
  const agentStatuses = ["Assigned", "In Progress", "Completed", "Approved"];

  // Steps for the stepper
  const steps = [
    "Basic Information",
    "Vehicle Details",
    "Agent & Inspection",
    "Images & Documents",
  ];

  // Fetch case details
  const fetchCaseDetails = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${Baseurl}/api/cases/individualCaseDetails/${id}`,
      );
      if (response.data.success) {
        setCaseData(response.data.caseDetails);
      } else {
        setError("Failed to fetch case details");
      }
    } catch (err) {
      setError(err.response?.data?.message || "Error fetching case details");
    } finally {
      setLoading(false);
    }
  };

  // Fetch employees (agents and inspectors)
  const fetchEmployees = async () => {
    try {
      const response = await axios.get(`${Baseurl}/api/employees/all`);
      if (response.data.success) {
        const employees = response.data.employees || response.data.data;
        // Filter agents (you might need to adjust based on your employee roles)
        const agentList = employees.filter(
          (emp) => emp.role === "agent" || emp.department === "field",
        );
        const inspectorList = employees.filter(
          (emp) => emp.role === "inspector" || emp.department === "inspection",
        );
        setAgents(agentList);
        setInspectors(inspectorList);
      }
    } catch (err) {
      console.error("Error fetching employees:", err);
    }
  };

  useEffect(() => {
    fetchCaseDetails();
    fetchEmployees();
  }, [id]);

  // File upload function
  const uploadFilesToServer = async (file, type) => {
    try {
      const formData = new FormData();
      formData.append("file", file);

      const res = await fetch(`${Imageurl}/upload`, {
        method: "POST",
        body: formData,
      });

      const data = await res.json();

      if (data && data.success) {
        return {
          success: true,
          filePath: data.filePath,
        };
      }
      return { success: false, error: "Upload failed" };
    } catch (error) {
      console.error(`${type} upload error:`, error);
      return {
        success: false,
        error: error.message,
      };
    }
  };

  // Handle single image upload for specific field
  const handleImageUpload = async (field, file) => {
    setUploading(true);
    try {
      if (!file) {
        throw new Error("No file selected");
      }

      const result = await uploadFilesToServer(file, "image");

      if (result.success) {
        // Handle different field types
        if (field.includes("signNdoc.")) {
          // For signature/document fields
          const docType = field.replace("signNdoc.", "");
          setCaseData((prev) => ({
            ...prev,
            signNdoc: {
              ...prev.signNdoc,
              [docType]: result.filePath,
            },
          }));
        } else {
          // For vehicle image fields
          const imageField = `${caseData.vehicleType}Images`;
          setCaseData((prev) => ({
            ...prev,
            [imageField]: {
              ...prev[imageField],
              [field]: result.filePath,
            },
          }));
        }

        setSuccess(`Image uploaded successfully for ${field}`);
      } else {
        setError(result.error || "Failed to upload image");
      }
    } catch (err) {
      setError("Error uploading image: " + err.message);
    } finally {
      setUploading(false);
      setShowImageModal(false);
      setNewImage(null);
      setImagePreview(null);
    }
  };

  // Dropzone configuration for single file upload
  const { getRootProps, getInputProps } = useDropzone({
    accept: {
      "image/*": [".jpeg", ".jpg", ".png", ".gif", ".webp"],
    },
    onDrop: (acceptedFiles) => {
      // Take only the first file
      const singleFile = acceptedFiles[0];
      setNewImage(singleFile);

      // Create preview for single file
      if (singleFile) {
        const preview = [
          Object.assign(singleFile, {
            preview: URL.createObjectURL(singleFile),
          }),
        ];
        setImagePreview(preview);
      } else {
        setImagePreview(null);
      }
    },
    maxFiles: 1,
  });

  // Handle input changes
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (type === "checkbox") {
      setCaseData((prev) => ({
        ...prev,
        [name]: checked,
      }));
    } else {
      setCaseData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  // Handle nested object changes
  const handleNestedChange = (path, value) => {
    setCaseData((prev) => {
      const keys = path.split(".");
      const updated = { ...prev };
      let current = updated;

      for (let i = 0; i < keys.length - 1; i++) {
        current = current[keys[i]] = { ...current[keys[i]] };
      }

      current[keys[keys.length - 1]] = value;
      return updated;
    });
  };

  // Handle array field changes (like accessories)
  const handleAccessoryChange = (accessoryType, accessoryName, checked) => {
    setCaseData((prev) => {
      const updated = { ...prev };
      if (updated[accessoryType]) {
        updated[accessoryType][accessoryName].value = checked;
      }
      return updated;
    });
  };

  // Handle required assets change
  const handleRequiredAssetChange = (index, checked) => {
    setCaseData((prev) => {
      const updated = { ...prev };
      if (updated.requiredAssets && updated.requiredAssets[index]) {
        updated.requiredAssets[index].value = checked;
      }
      return updated;
    });
  };

  // Save case data
  const handleSave = async () => {
    try {
      setSaving(true);
      setError("");
      setSuccess("");

      // Prepare data for update
      const updateData = {
        _id: id,
        ...caseData,
      };

      const response = await axios.put(
        `${Baseurl}/api/cases/updateCase/${id}`,
        updateData,
      );

      if (response.data.success) {
        setSuccess("Case updated successfully!");
        // Refresh case data
        fetchCaseDetails();
      } else {
        setError(response.data.message || "Failed to update case");
      }
    } catch (err) {
      setError(err.response?.data?.message || "Error updating case");
    } finally {
      setSaving(false);
    }
  };

  // Format label for display
  const formatLabel = (text) => {
    return text
      .replace(/([A-Z])/g, " $1")
      .replace(/^\w/, (c) => c.toUpperCase());
  };

  // Render image upload modal
  const renderImageUploadModal = () => (
    <Modal
      show={showImageModal}
      onHide={() => {
        setShowImageModal(false);
        setNewImage(null);
        setImagePreview(null);
      }}
      size="lg"
    >
      <Modal.Header closeButton>
        <Modal.Title>Upload Image for {currentImageType}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div
          {...getRootProps()}
          className="border-dashed border-2 border-gray-300 rounded p-4 text-center cursor-pointer bg-light"
          style={{
            borderStyle: "dashed",
            backgroundColor: "#fafafa",
            minHeight: "150px",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <input {...getInputProps()} />
          <FaUpload className="fs-1 text-secondary mb-3" />
          <p className="text-secondary mb-1">
            Drag & drop an image here, or click to select
          </p>
          <small className="text-secondary">
            Supports: JPEG, PNG, GIF, WebP (Single image only)
          </small>
        </div>

        {imagePreview && imagePreview.length > 0 && (
          <div className="mt-4">
            <h6>Selected Image</h6>
            <Row className="g-2 mt-2">
              <Col xs={12} md={6}>
                <div
                  className="position-relative rounded overflow-hidden border"
                  style={{ height: "200px" }}
                >
                  <img
                    src={imagePreview[0].preview}
                    alt="Preview"
                    className="w-100 h-100 object-fit-contain p-2"
                  />
                  <Button
                    variant="dark"
                    size="sm"
                    className="position-absolute top-0 end-0 opacity-75"
                    style={{ transform: "translate(50%, -50%)" }}
                    onClick={() => {
                      setImagePreview(null);
                      setNewImage(null);
                    }}
                  >
                    <FaTrash size={12} />
                  </Button>
                </div>
                <div className="text-center mt-2">
                  <small className="text-muted">{imagePreview[0].name}</small>
                </div>
              </Col>
            </Row>
          </div>
        )}

        {uploading && (
          <div className="mt-3">
            <p className="mb-2">Uploading...</p>
            <Spinner animation="border" size="sm" />
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button
          variant="secondary"
          onClick={() => {
            setShowImageModal(false);
            setNewImage(null);
            setImagePreview(null);
          }}
        >
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={() => handleImageUpload(currentImageType, newImage)}
          disabled={!newImage || uploading}
        >
          Upload
        </Button>
      </Modal.Footer>
    </Modal>
  );

  // Render condition fields based on vehicle type
  const renderConditionFields = () => {
    if (!caseData || !caseData.vehicleType) return null;

    const conditionFields = caseData[caseData.vehicleType];
    if (!conditionFields) return null;

    return Object.entries(conditionFields).map(([section, fields]) => (
      <Card key={section} className="mb-3">
        <Card.Body>
          <Card.Title className="text-capitalize">
            {formatLabel(section)}
          </Card.Title>
          <Row className="g-3">
            {Object.entries(fields).map(([field, config]) => (
              <Col xs={12} md={6} key={field}>
                {config.formType === "dropdown" ? (
                  <Form.Group>
                    <Form.Label>{formatLabel(field)}</Form.Label>
                    <Form.Select
                      value={config.value || ""}
                      onChange={(e) =>
                        handleNestedChange(
                          `${caseData.vehicleType}.${section}.${field}.value`,
                          e.target.value,
                        )
                      }
                    >
                      <option value="">Select...</option>
                      {config.options?.map((option) => (
                        <option key={option} value={option}>
                          {option}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                ) : config.formType === "radio" ? (
                  <Form.Group>
                    <Form.Label className="d-block">
                      {formatLabel(field)}
                    </Form.Label>
                    <div className="d-flex flex-wrap gap-3">
                      {config.options?.map((option) => (
                        <Form.Check
                          key={option}
                          type="radio"
                          name={`${section}-${field}`}
                          label={option}
                          checked={config.value === option}
                          onChange={(e) =>
                            handleNestedChange(
                              `${caseData.vehicleType}.${section}.${field}.value`,
                              e.target.checked ? option : "",
                            )
                          }
                        />
                      ))}
                    </div>
                  </Form.Group>
                ) : config.formType === "checkbox" ? (
                  <Form.Group>
                    <Form.Label className="d-block">
                      {formatLabel(field)}
                    </Form.Label>
                    <div className="d-flex flex-column gap-2">
                      {config.options?.map((option) => (
                        <Form.Check
                          key={option}
                          type="checkbox"
                          label={option}
                          checked={(config.value || []).includes(option)}
                          onChange={(e) => {
                            const currentValues = [...(config.value || [])];
                            if (e.target.checked) {
                              currentValues.push(option);
                            } else {
                              const index = currentValues.indexOf(option);
                              if (index > -1) {
                                currentValues.splice(index, 1);
                              }
                            }
                            handleNestedChange(
                              `${caseData.vehicleType}.${section}.${field}.value`,
                              currentValues,
                            );
                          }}
                        />
                      ))}
                    </div>
                  </Form.Group>
                ) : (
                  <Form.Group>
                    <Form.Label>{formatLabel(field)}</Form.Label>
                    <Form.Control
                      type="text"
                      value={config.value || ""}
                      onChange={(e) =>
                        handleNestedChange(
                          `${caseData.vehicleType}.${section}.${field}.value`,
                          e.target.value,
                        )
                      }
                    />
                  </Form.Group>
                )}
              </Col>
            ))}
          </Row>
        </Card.Body>
      </Card>
    ));
  };

  // Render accessories based on vehicle type
  const renderAccessories = () => {
    if (!caseData || !caseData.vehicleType) return null;

    const accessoryType = `${caseData.vehicleType.replace("ev", "").toLowerCase()}Accessories`;
    const accessories = caseData[accessoryType];

    if (!accessories) return null;

    return (
      <Card className="mb-3">
        <Card.Body>
          <Card.Title>Accessories</Card.Title>
          <Row className="g-3">
            {Object.entries(accessories).map(([name, config]) => (
              <Col xs={12} md={6} lg={4} key={name}>
                <Form.Check
                  type="checkbox"
                  label={formatLabel(name)}
                  checked={config.value || false}
                  onChange={(e) =>
                    handleNestedChange(
                      `${accessoryType}.${name}.value`,
                      e.target.checked,
                    )
                  }
                />
              </Col>
            ))}
          </Row>
        </Card.Body>
      </Card>
    );
  };

  // Render images section
  const renderImagesSection = () => {
    if (!caseData || !caseData.vehicleType) return null;

    const imageField = `${caseData.vehicleType}Images`;
    const images = caseData[imageField];

    if (!images) return null;

    return (
      <div>
        <h5 className="mb-3">Vehicle Images</h5>
        <Row className="g-3">
          {Object.entries(images).map(([imageType, url]) => (
            <Col xs={12} md={6} lg={4} key={imageType}>
              <Card>
                <Card.Body>
                  <Card.Title className="fs-6">
                    {formatLabel(imageType)}
                  </Card.Title>
                  {url ? (
                    <div className="position-relative">
                      <img
                        src={url}
                        alt={imageType}
                        className="w-100 rounded"
                        style={{ height: "150px", objectFit: "cover" }}
                      />
                      <div className="position-absolute top-0 end-0 p-2 d-flex gap-1">
                        <OverlayTrigger
                          placement="top"
                          overlay={<Tooltip>Replace Image</Tooltip>}
                        >
                          <Button
                            size="sm"
                            variant="dark"
                            className="opacity-75"
                            onClick={() => {
                              setCurrentImageType(imageType);
                              setShowImageModal(true);
                            }}
                          >
                            <FaEdit size={14} />
                          </Button>
                        </OverlayTrigger>
                        <OverlayTrigger
                          placement="top"
                          overlay={<Tooltip>View Image</Tooltip>}
                        >
                          <Button
                            size="sm"
                            variant="dark"
                            className="opacity-75"
                            onClick={() => window.open(url, "_blank")}
                          >
                            <FaEye size={14} />
                          </Button>
                        </OverlayTrigger>
                      </div>
                    </div>
                  ) : (
                    <Button
                      variant="outline-primary"
                      className="w-100 d-flex align-items-center justify-content-center gap-2"
                      onClick={() => {
                        setCurrentImageType(imageType);
                        setShowImageModal(true);
                      }}
                    >
                      <FaCamera /> Upload Image
                    </Button>
                  )}
                </Card.Body>
              </Card>
            </Col>
          ))}
        </Row>
      </div>
    );
  };

  // Render required assets
  const renderRequiredAssets = () => {
    if (!caseData || !caseData.requiredAssets) return null;

    return (
      <Card className="mb-3">
        <Card.Body>
          <Card.Title>Required Assets</Card.Title>
          <Row className="g-3">
            {caseData.requiredAssets.map((asset, index) => (
              <Col xs={12} md={6} lg={4} key={asset.key}>
                <Card className="border">
                  <Card.Body>
                    <Stack direction="horizontal" gap={3}>
                      <div
                        className="rounded-circle d-flex align-items-center justify-content-center"
                        style={{
                          width: "40px",
                          height: "40px",
                          backgroundColor: asset.color,
                          color: "white",
                          flexShrink: 0,
                        }}
                      >
                        {asset.icon}
                      </div>
                      <div className="flex-grow-1">
                        <h6 className="mb-0">{asset.name}</h6>
                      </div>
                      <Form.Check
                        type="checkbox"
                        checked={asset.value || false}
                        onChange={(e) =>
                          handleRequiredAssetChange(index, e.target.checked)
                        }
                      />
                    </Stack>
                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row>
        </Card.Body>
      </Card>
    );
  };

  // Render basic information tab
  const renderBasicInfo = () => (
    <Row className="g-3">
      <Col xs={12}>
        <h5>Case Information</h5>
      </Col>

      <Col xs={12} md={6}>
        <Form.Group>
          <Form.Label>Case ID</Form.Label>
          <Form.Control
            type="text"
            value={caseData?.caseID || ""}
            disabled
            readOnly
          />
        </Form.Group>
      </Col>

      <Col xs={12} md={6}>
        <Form.Group>
          <Form.Label>Case Type</Form.Label>
          <Form.Select
            name="caseType"
            value={caseData?.caseType || ""}
            onChange={handleInputChange}
          >
            <option value="">Select Case Type</option>
            {caseTypes.map((type) => (
              <option key={type} value={type}>
                {type}
              </option>
            ))}
          </Form.Select>
        </Form.Group>
      </Col>

      <Col xs={12} md={6}>
        <Form.Group>
          <Form.Label>Vehicle Type</Form.Label>
          <Form.Select
            name="vehicleType"
            value={caseData?.vehicleType || ""}
            onChange={handleInputChange}
          >
            <option value="">Select Vehicle Type</option>
            {vehicleTypes.map((type) => (
              <option key={type} value={type}>
                {type.replace("ev", "EV ").replace(/([A-Z])/g, " $1")}
              </option>
            ))}
          </Form.Select>
        </Form.Group>
      </Col>

      <Col xs={12} md={6}>
        <Form.Group>
          <Form.Label>Valuation Purpose</Form.Label>
          <Form.Control
            type="text"
            name="valuationPurpose"
            value={caseData?.valuationPurpose || ""}
            onChange={handleInputChange}
          />
        </Form.Group>
      </Col>

      <Col xs={12}>
        <hr className="my-4" />
        <h5>Requester Information</h5>
      </Col>

      <Col xs={12} md={6}>
        <Form.Group>
          <Form.Label>Requester Name</Form.Label>
          <Form.Control
            type="text"
            name="requesterName"
            value={caseData?.requesterName || ""}
            onChange={handleInputChange}
          />
        </Form.Group>
      </Col>

      <Col xs={12} md={6}>
        <Form.Group>
          <Form.Label>Requester Branch</Form.Label>
          <Form.Control
            type="text"
            name="requesterBranch"
            value={caseData?.requesterBranch || ""}
            onChange={handleInputChange}
          />
        </Form.Group>
      </Col>

      <Col xs={12} md={6}>
        <Form.Group>
          <Form.Label>Requester Phone</Form.Label>
          <Form.Control
            type="tel"
            name="requesterPhone"
            value={caseData?.requesterPhone || ""}
            onChange={handleInputChange}
          />
        </Form.Group>
      </Col>

      <Col xs={12}>
        <hr className="my-4" />
        <h5>Borrower Information</h5>
      </Col>

      <Col xs={12} md={6}>
        <Form.Group>
          <Form.Label>Borrower Name *</Form.Label>
          <Form.Control
            type="text"
            name="borrowerName"
            value={caseData?.borrowerName || ""}
            onChange={handleInputChange}
            required
          />
        </Form.Group>
      </Col>

      <Col xs={12} md={6}>
        <Form.Group>
          <Form.Label>Borrower Phone *</Form.Label>
          <Form.Control
            type="tel"
            name="borrowerPhoneNo"
            value={caseData?.borrowerPhoneNo || ""}
            onChange={handleInputChange}
            required
          />
        </Form.Group>
      </Col>

      <Col xs={12} md={6}>
        <Form.Group>
          <Form.Label>Borrower Email</Form.Label>
          <Form.Control
            type="email"
            name="borrowerEmail"
            value={caseData?.borrowerEmail || ""}
            onChange={handleInputChange}
          />
        </Form.Group>
      </Col>

      <Col xs={12}>
        <Form.Group>
          <Form.Label>Borrower Address *</Form.Label>
          <Form.Control
            as="textarea"
            rows={2}
            name="borrowerAddress"
            value={caseData?.borrowerAddress || ""}
            onChange={handleInputChange}
            required
          />
        </Form.Group>
      </Col>
    </Row>
  );

  // Render vehicle details tab
  const renderVehicleDetails = () => (
    <Row className="g-3">
      <Col xs={12}>
        <h5>Vehicle Specifications</h5>
      </Col>

      <Col xs={12} md={6} lg={4}>
        <Form.Group>
          <Form.Label>Brand</Form.Label>
          <Form.Control
            type="text"
            name="brand"
            value={caseData?.brand || ""}
            onChange={handleInputChange}
          />
        </Form.Group>
      </Col>

      <Col xs={12} md={6} lg={4}>
        <Form.Group>
          <Form.Label>Model</Form.Label>
          <Form.Control
            type="text"
            name="model"
            value={caseData?.model || ""}
            onChange={handleInputChange}
          />
        </Form.Group>
      </Col>

      <Col xs={12} md={6} lg={4}>
        <Form.Group>
          <Form.Label>Variant</Form.Label>
          <Form.Control
            type="text"
            name="variant"
            value={caseData?.variant || ""}
            onChange={handleInputChange}
          />
        </Form.Group>
      </Col>

      <Col xs={12} md={6}>
        <Form.Group>
          <Form.Label>Registration Number</Form.Label>
          <Form.Control
            type="text"
            name="regdNo"
            value={caseData?.regdNo || ""}
            onChange={handleInputChange}
          />
        </Form.Group>
      </Col>

      <Col xs={12} md={6}>
        <Form.Group>
          <Form.Label>Chassis Number</Form.Label>
          <Form.Control
            type="text"
            name="chasisNo"
            value={caseData?.chasisNo || ""}
            onChange={handleInputChange}
          />
        </Form.Group>
      </Col>

      <Col xs={12} md={6}>
        <Form.Group>
          <Form.Label>Engine Number</Form.Label>
          <Form.Control
            type="text"
            name="engNo"
            value={caseData?.engNo || ""}
            onChange={handleInputChange}
          />
        </Form.Group>
      </Col>

      <Col xs={12} md={6}>
        <Form.Group>
          <Form.Label>Manufacturing Date</Form.Label>
          <Form.Control
            type="date"
            name="manufacturingDate"
            value={caseData?.manufacturingDate || ""}
            onChange={handleInputChange}
          />
        </Form.Group>
      </Col>

      <Col xs={12} md={6}>
        <Form.Group>
          <Form.Label>Registration Date</Form.Label>
          <Form.Control
            type="date"
            name="regdDate"
            value={caseData?.regdDate || ""}
            onChange={handleInputChange}
          />
        </Form.Group>
      </Col>

      <Col xs={12} md={6}>
        <Form.Group>
          <Form.Label>Vehicle Color</Form.Label>
          <Form.Control
            type="text"
            name="vehicleColor"
            value={caseData?.vehicleColor || ""}
            onChange={handleInputChange}
          />
        </Form.Group>
      </Col>

      <Col xs={12}>
        <hr className="my-4" />
        <h5>Insurance & Financial Details</h5>
      </Col>

      <Col xs={12} md={6}>
        <Form.Group>
          <Form.Label>Insurance Company</Form.Label>
          <Form.Control
            type="text"
            name="insuranceCompany"
            value={caseData?.insuranceCompany || ""}
            onChange={handleInputChange}
          />
        </Form.Group>
      </Col>

      <Col xs={12} md={6}>
        <Form.Group>
          <Form.Label>Insurance Status</Form.Label>
          <Form.Control
            type="text"
            name="insuranceStatus"
            value={caseData?.insuranceStatus || ""}
            onChange={handleInputChange}
          />
        </Form.Group>
      </Col>

      <Col xs={12} md={6}>
        <Form.Group>
          <Form.Label>Loan Agreement No</Form.Label>
          <Form.Control
            type="text"
            name="loanAgreementNo"
            value={caseData?.loanAgreementNo || ""}
            onChange={handleInputChange}
          />
        </Form.Group>
      </Col>

      <Col xs={12} md={6}>
        <Form.Group>
          <Form.Label>Hypothecation Bank</Form.Label>
          <Form.Control
            type="text"
            name="hypothecationBank"
            value={caseData?.hypothecationBank || ""}
            onChange={handleInputChange}
          />
        </Form.Group>
      </Col>

      <Col xs={12}>
        <hr className="my-4" />
        <h5>Vehicle Condition</h5>
      </Col>

      {renderConditionFields()}
      {renderAccessories()}
    </Row>
  );

  // Render agent and inspection tab
  const renderAgentInspection = () => (
    <Row className="g-3">
      <Col xs={12}>
        <h5>Agent Assignment</h5>
      </Col>

      <Col xs={12} md={6}>
        <Form.Group>
          <Form.Label>Assigned Agent</Form.Label>
          <Form.Select
            value={caseData?.assignedAgent?.id || ""}
            onChange={(e) =>
              handleNestedChange("assignedAgent.id", e.target.value)
            }
          >
            <option value="">Select an agent</option>
            {agents.map((agent) => (
              <option key={agent._id} value={agent._id}>
                {agent.name} ({agent.email})
              </option>
            ))}
          </Form.Select>
        </Form.Group>
      </Col>

      <Col xs={12} md={6}>
        <Form.Group>
          <Form.Label>Agent Status</Form.Label>
          <Form.Select
            value={caseData?.assignedAgent?.status || ""}
            onChange={(e) =>
              handleNestedChange("assignedAgent.status", e.target.value)
            }
          >
            <option value="">Select status</option>
            {agentStatuses.map((status) => (
              <option key={status} value={status}>
                {status}
              </option>
            ))}
          </Form.Select>
        </Form.Group>
      </Col>

      <Col xs={12}>
        <Form.Group>
          <Form.Label>Agent Observation</Form.Label>
          <Form.Control
            as="textarea"
            rows={3}
            value={caseData?.assignedAgent?.observation || ""}
            onChange={(e) =>
              handleNestedChange("assignedAgent.observation", e.target.value)
            }
          />
        </Form.Group>
      </Col>

      <Col xs={12}>
        <hr className="my-4" />
        <h5>Vehicle Status & Location</h5>
      </Col>

      <Col xs={12} md={6}>
        <Form.Group>
          <Form.Label>Vehicle Status</Form.Label>
          <Form.Select
            name="vehicleStatus"
            value={caseData?.vehicleStatus || ""}
            onChange={handleInputChange}
          >
            <option value="">Select status</option>
            {vehicleStatuses.map((status) => (
              <option key={status} value={status}>
                {status}
              </option>
            ))}
          </Form.Select>
        </Form.Group>
      </Col>

      <Col xs={12}>
        <hr className="my-4" />
        <h5>Inspections</h5>
      </Col>

      {caseData?.inspections?.map((inspection, index) => (
        <Col xs={12} key={index}>
          <Card className="border mb-3">
            <Card.Body>
              <Stack
                direction="horizontal"
                className="justify-content-between mb-3"
              >
                <h6 className="mb-0">Inspection #{index + 1}</h6>
                <Badge
                  bg={
                    inspection.status === "completed"
                      ? "success"
                      : inspection.status === "in-progress"
                        ? "warning"
                        : "secondary"
                  }
                >
                  {inspection.status}
                </Badge>
              </Stack>

              <Row className="g-3">
                <Col xs={12} md={6}>
                  <Form.Group>
                    <Form.Label>Inspector</Form.Label>
                    <Form.Select
                      value={inspection.inspectionDoneBy || ""}
                      onChange={(e) => {
                        const updatedInspections = [
                          ...(caseData.inspections || []),
                        ];
                        updatedInspections[index] = {
                          ...updatedInspections[index],
                          inspectionDoneBy: e.target.value || null,
                        };
                        handleNestedChange("inspections", updatedInspections);
                      }}
                    >
                      <option value="">Select inspector</option>
                      {inspectors.map((inspector) => (
                        <option key={inspector._id} value={inspector._id}>
                          {inspector.name} ({inspector.email})
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>

                <Col xs={12} md={6}>
                  <Form.Group>
                    <Form.Label>Inspection Date</Form.Label>
                    <Form.Control
                      type="date"
                      value={inspection.lastInspection?.split("T")[0] || ""}
                      onChange={(e) => {
                        const updatedInspections = [
                          ...(caseData.inspections || []),
                        ];
                        updatedInspections[index] = {
                          ...updatedInspections[index],
                          lastInspection: e.target.value,
                        };
                        handleNestedChange("inspections", updatedInspections);
                      }}
                    />
                  </Form.Group>
                </Col>

                <Col xs={12}>
                  <Form.Group>
                    <Form.Label>Observation</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={2}
                      value={inspection.inspectionObservation || ""}
                      onChange={(e) => {
                        const updatedInspections = [
                          ...(caseData.inspections || []),
                        ];
                        updatedInspections[index] = {
                          ...updatedInspections[index],
                          inspectionObservation: e.target.value,
                        };
                        handleNestedChange("inspections", updatedInspections);
                      }}
                    />
                  </Form.Group>
                </Col>

                <Col xs={12}>
                  <Form.Group>
                    <Form.Label>Notes</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={2}
                      value={inspection.notes || ""}
                      onChange={(e) => {
                        const updatedInspections = [
                          ...(caseData.inspections || []),
                        ];
                        updatedInspections[index] = {
                          ...updatedInspections[index],
                          notes: e.target.value,
                        };
                        handleNestedChange("inspections", updatedInspections);
                      }}
                    />
                  </Form.Group>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Col>
      ))}

      <Col xs={12}>
        <Button
          variant="outline-primary"
          onClick={() => {
            const newInspection = {
              lastInspection: new Date().toISOString().split("T")[0],
              inspectionDoneBy: null,
              inspectionObservation: "",
              status: "pending",
              images: [],
              notes: "",
            };
            const updatedInspections = [
              ...(caseData?.inspections || []),
              newInspection,
            ];
            handleNestedChange("inspections", updatedInspections);
          }}
          className="d-flex align-items-center gap-2"
        >
          <FaPlus /> Add New Inspection
        </Button>
      </Col>
    </Row>
  );

  // Render images and documents tab
  const renderImagesDocuments = () => (
    <Row className="g-3">
      <Col xs={12}>{renderImagesSection()}</Col>

      <Col xs={12}>
        <hr className="my-4" />
        {renderRequiredAssets()}
      </Col>

      <Col xs={12}>
        <hr className="my-4" />
        <h5>Videos</h5>
        <Form.Group>
          <Form.Label>Video URL</Form.Label>
          <Form.Control
            type="text"
            name="videos"
            value={caseData?.videos || ""}
            onChange={handleInputChange}
            placeholder="Enter video URL or upload video"
          />
          <Form.Text className="text-muted">
            For video uploads, please use the upload functionality
          </Form.Text>
        </Form.Group>
      </Col>

      <Col xs={12}>
        <hr className="my-4" />
        <h5>Signatures & Documents</h5>
      </Col>

      {caseData?.signNdoc &&
        Object.entries(caseData.signNdoc).map(([docType, url]) => (
          <Col xs={12} md={6} lg={4} key={docType}>
            <Card>
              <Card.Body>
                <Card.Title className="fs-6">{formatLabel(docType)}</Card.Title>
                {url ? (
                  <div className="position-relative">
                    <img
                      src={url}
                      alt={docType}
                      className="w-100 rounded"
                      style={{ height: "150px", objectFit: "cover" }}
                    />
                    <div className="position-absolute top-0 end-0 p-2 d-flex gap-1">
                      <OverlayTrigger
                        placement="top"
                        overlay={<Tooltip>Replace Document</Tooltip>}
                      >
                        <Button
                          size="sm"
                          variant="dark"
                          className="opacity-75"
                          onClick={() => {
                            setCurrentImageType(`signNdoc.${docType}`);
                            setShowImageModal(true);
                          }}
                        >
                          <FaEdit size={14} />
                        </Button>
                      </OverlayTrigger>
                      <OverlayTrigger
                        placement="top"
                        overlay={<Tooltip>View Document</Tooltip>}
                      >
                        <Button
                          size="sm"
                          variant="dark"
                          className="opacity-75"
                          onClick={() => window.open(url, "_blank")}
                        >
                          <FaEye size={14} />
                        </Button>
                      </OverlayTrigger>
                    </div>
                  </div>
                ) : (
                  <Button
                    variant="outline-primary"
                    className="w-100 d-flex align-items-center justify-content-center gap-2"
                    onClick={() => {
                      setCurrentImageType(`signNdoc.${docType}`);
                      setShowImageModal(true);
                    }}
                  >
                    <FaUpload /> Upload Document
                  </Button>
                )}
              </Card.Body>
            </Card>
          </Col>
        ))}
    </Row>
  );

  if (loading) {
    return (
      <Container className="my-4">
        <div
          className="d-flex justify-content-center align-items-center"
          style={{ minHeight: "60vh" }}
        >
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Loading...</span>
          </Spinner>
        </div>
      </Container>
    );
  }

  if (!caseData) {
    return (
      <Container className="my-4">
        <Alert variant="danger">Case not found</Alert>
        <Button
          variant="outline-secondary"
          onClick={() => navigate(-1)}
          className="d-flex align-items-center gap-2 mt-3"
        >
          <FaArrowLeft /> Go Back
        </Button>
      </Container>
    );
  }

  // Get status badge color
  const getStatusBadgeColor = (status) => {
    switch (status) {
      case "Parked":
        return "success";
      case "Maintenance":
        return "warning";
      case "Service Requested":
        return "info";
      default:
        return "secondary";
    }
  };

  return (
    <Container className="my-4">
      {/* Header */}
      <div className="mb-4">
        <Stack direction="horizontal" gap={3} className="mb-3">
          <Button
            variant="outline-secondary"
            onClick={() => navigate(-1)}
            className="d-flex align-items-center"
          >
            <FaArrowLeft />
          </Button>
          <h1 className="mb-0">Edit Case: {caseData.caseID}</h1>
          <Badge
            bg={getStatusBadgeColor(caseData.vehicleStatus)}
            className="fs-6"
          >
            {caseData.vehicleStatus}
          </Badge>
        </Stack>

        {/* Stepper */}
        <div className="d-flex align-items-center mb-3">
          {steps.map((step, index) => (
            <React.Fragment key={step}>
              <div className="d-flex align-items-center">
                <div
                  className={`rounded-circle d-flex align-items-center justify-content-center ${
                    index === activeStep
                      ? "bg-primary text-white"
                      : index < activeStep
                        ? "bg-success text-white"
                        : "bg-light text-secondary"
                  }`}
                  style={{ width: "32px", height: "32px" }}
                >
                  {index < activeStep ? <FaCheck /> : index + 1}
                </div>
                <span className="ms-2">{step}</span>
              </div>
              {index < steps.length - 1 && (
                <div className="flex-grow-1 mx-2">
                  <hr className="my-0" />
                </div>
              )}
            </React.Fragment>
          ))}
        </div>
      </div>

      {/* Success/Error Messages */}
      {error && (
        <Alert
          variant="danger"
          className="mb-3"
          dismissible
          onClose={() => setError("")}
        >
          {error}
        </Alert>
      )}
      {success && (
        <Alert
          variant="success"
          className="mb-3"
          dismissible
          onClose={() => setSuccess("")}
        >
          {success}
        </Alert>
      )}

      {/* Tabs Navigation */}
      <Card className="mb-3">
        <Card.Body className="p-0">
          <Tabs
            activeKey={tabValue}
            onSelect={(k) => setTabValue(Number(k))}
            className="px-3 pt-2"
          >
            <Tab eventKey={0} title="Basic Information" />
            <Tab eventKey={1} title="Vehicle Details" />
            <Tab eventKey={2} title="Agent & Inspection" />
            <Tab eventKey={3} title="Images & Documents" />
          </Tabs>
        </Card.Body>
      </Card>

      {/* Tab Content */}
      <Card className="mb-3">
        <Card.Body>
          {tabValue === 0 && renderBasicInfo()}
          {tabValue === 1 && renderVehicleDetails()}
          {tabValue === 2 && renderAgentInspection()}
          {tabValue === 3 && renderImagesDocuments()}
        </Card.Body>
      </Card>

      {/* Action Buttons */}
      <div className="d-flex justify-content-between mb-4">
        <Button
          variant="outline-secondary"
          onClick={() => navigate(-1)}
          className="d-flex align-items-center gap-2"
        >
          <FaArrowLeft /> Cancel
        </Button>
        <Stack direction="horizontal" gap={3}>
          <Button
            variant="outline-warning"
            onClick={() => {
              const confirmed = window.confirm(
                "Are you sure you want to reset all changes?",
              );
              if (confirmed) {
                fetchCaseDetails();
                setSuccess("Changes reset successfully");
              }
            }}
          >
            Reset Changes
          </Button>
          <Button
            variant="primary"
            onClick={handleSave}
            disabled={saving}
            className="d-flex align-items-center gap-2"
          >
            {saving ? (
              <>
                <Spinner animation="border" size="sm" /> Saving...
              </>
            ) : (
              <>
                <FaSave /> Save Changes
              </>
            )}
          </Button>
        </Stack>
      </div>

      {/* Image Upload Modal */}
      {renderImageUploadModal()}
    </Container>
  );
};

export default EditCase;
